import React from "react";
import { EMPTY, fixed, pyRound, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";

// A stat's game-to-game range on one row (the athlete's Consistency):
// label | low | track | high. The track spans the player's lowest to
// highest game: the whole track in bm-orange at 20%, the middle half of the
// games (25th to 75th percentile) as a bm-orange capsule (at least 4% wide),
// and the per-game average as an ink dot with a white ring and its value
// above it. The quartiles are printed under the capsule ends when it is
// wide enough, else one centred "14–24"; when they are equal it reads
// "typical 0 (13 of 17 games at 0)" from `typicalNote`. Every number is on
// screen; the aria-label says the row in words.
//
//   RangeTrack({ label, min, p25, p75, max, mean, typicalNote, format, formatMean, phone })
//
// format(v)     the low, high and quartiles (default: whole numbers)
// formatMean(v) the average (default: one decimal)
// Phones narrow the label and end columns (70 / 30px).

const cx = (...parts) => parts.filter(Boolean).join(" ");

const whole = (v) => {
  const n = pyRound(v);
  return n === null ? EMPTY : String(n);
};
const oneDecimal = (v) => fixed(v, 1);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const rangeLabel = ({ label, min, p25, p75, max, mean }, format = whole, formatMean = oneDecimal) =>
  `${label}: low ${format(min)}, middle half ${format(p25)} to ${format(p75)}, average ${formatMean(mean)}, high ${format(max)}`;

export const RangeTrack = ({ label, min, p25, p75, max, mean, typicalNote, format = whole, formatMean = oneDecimal, phone: phoneProp }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  const lo = toNum(min);
  const hi = toNum(max);
  const q1 = toNum(p25);
  const q3 = toNum(p75);
  const avg = toNum(mean);
  if (lo === null || hi === null) return null;
  const span = Math.max(1, hi - lo);
  const at = (v) => ((v - lo) / span) * 100;
  const boxLeft = q1 === null ? 0 : clamp(at(q1), 0, 100);
  const boxRight = q3 === null ? boxLeft : clamp(at(q3), 0, 100);
  const boxWidth = Math.max(4, boxRight - boxLeft);
  const meanAt = avg === null ? null : clamp(at(avg), 3, 97);
  const split = q1 !== null && q3 !== null && q3 > q1 && boxRight - boxLeft >= 14;
  let centred = null;
  let typical = false;
  if (!split && q1 !== null) {
    typical = !(q3 !== null && q3 > q1);
    centred = typical ? `typical ${format(q1)}${typicalNote ? ` (${typicalNote})` : ""}` : `${format(q1)}\u2013${format(q3)}`;
  }
  const centreAt = q1 === null ? 50 : clamp(at((q1 + (q3 ?? q1)) / 2), 15, 85);

  return (
    <div className={cx("grid items-center gap-2", phone ? "grid-cols-[70px_30px_minmax(0,1fr)_30px]" : "grid-cols-[84px_34px_minmax(0,1fr)_34px]")}>
      <span className={cx("break-words text-bm-ink", phone ? "bm-label-m" : "bm-label-l")}>{label}</span>
      <span className="text-center bm-body-s text-bm-muted">{format(lo)}</span>
      <div
        role="img"
        aria-label={rangeLabel({ label, min: lo, p25: q1, p75: q3, max: hi, mean: avg }, format, formatMean)}
        className={cx("relative mt-[22px] h-[14px] rounded-full bg-bm-orange/20", typical && phone ? "mb-[34px]" : "mb-[22px]")}
      >
        {/* No quartiles, no capsule: the middle half is never made up. */}
        {q1 !== null && (
          <span aria-hidden="true" className="absolute inset-y-0 rounded-full bg-bm-orange" style={{ left: `${Math.min(boxLeft, 100 - boxWidth)}%`, width: `${boxWidth}%` }} />
        )}
        {meanAt !== null && (
          <>
            <span
              aria-hidden="true"
              className="absolute top-1/2 h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[2.5px] border-white bg-bm-ink shadow-[0_0_0_1px_rgba(0,0,0,0.12)]"
              style={{ left: `${meanAt}%` }}
            />
            <span
              aria-hidden="true"
              className="absolute bottom-[calc(100%+5px)] -translate-x-1/2 whitespace-nowrap font-archivo text-[13px] font-extrabold leading-4 text-bm-ink"
              style={{ left: `${meanAt}%` }}
            >
              {formatMean(avg)}
            </span>
          </>
        )}
        {split && (
          <>
            <span aria-hidden="true" className="absolute top-[calc(100%+4px)] -translate-x-1/2 whitespace-nowrap bm-label-xs text-bm-orange" style={{ left: `${boxLeft}%` }}>
              {format(q1)}
            </span>
            <span aria-hidden="true" className="absolute top-[calc(100%+4px)] -translate-x-1/2 whitespace-nowrap bm-label-xs text-bm-orange" style={{ left: `${boxRight}%` }}>
              {format(q3)}
            </span>
          </>
        )}
        {centred && !typical && (
          <span aria-hidden="true" className="absolute top-[calc(100%+4px)] -translate-x-1/2 whitespace-nowrap bm-label-xs text-bm-orange" style={{ left: `${centreAt}%` }}>
            {centred}
          </span>
        )}
        {/* The typical-value note is long: it takes the track's width and wraps. */}
        {centred && typical && (
          <span aria-hidden="true" className="absolute inset-x-0 top-[calc(100%+4px)] text-center bm-label-xs text-bm-orange">
            {centred}
          </span>
        )}
      </div>
      <span className="text-center bm-body-s text-bm-muted">{format(hi)}</span>
    </div>
  );
};

export default RangeTrack;
